/**
 * Что просить трейдера держать пред-подписанным.
 *
 * # Правило одной строкой
 *
 * Греется ВСЁ, чем оператор может нажать: пресеты панели плюс последняя сумма,
 * набранная в поле ввода.
 *
 * Ручная сумма входит в набор не для полноты. Она — такая же кнопка, и до
 * появления ключа presign:sizes_usd она была единственной кнопкой, ордер по
 * которой ГАРАНТИРОВАННО подписывался на горячем пути: трейдер грел список из
 * своего окружения и о набранном в браузере не знал ничего.
 *
 * # Почему «последняя», а не «текущая»
 *
 * Поле ввода помнит набранное между сессиями (settings.customSize), и вернуться к
 * своей сумме оператор может одним кликом по нему. Греть её только в режиме
 * custom значило бы остудить её ровно в момент, когда он ткнул в пресет, чтобы
 * через секунду вернуться назад — то есть на каждом переключении.
 *
 * # Модуль чистый
 *
 * Ни сети, ни React: сеть живёт в hooks/useWarmSizes.ts. Разделение то же, что у
 * presets.ts / SizePresetsEditor и settings.ts / useSettings, и по той же
 * причине — правила проверяются обычным тестом, без jsdom и без fetch.
 */

import { format, parse } from "./money";
import { MAX_WARM_SIZES, normalizeSizes } from "./presets";
import type { Settings } from "./settings";

export interface WarmRequest {
  /** Что греть: пресеты вместе с последней ручной суммой. */
  sizes_usd: string[];
  /** Только пресеты: ими web_api засеет свежую вкладку. */
  presets_usd: string[];
}

/**
 * Набор для трейдера из настроек оператора.
 *
 * Суммы канонизируются через money.format — тем же правилом, каким их
 * канонизирует activeSizeOf перед отправкой ордера. Разойдись эти два правила, и
 * «10.0» из поля ввода попало бы в ключ строкой, которой нет в билете: кэш
 * промахивался бы при совпадающих ЗНАЧЕНИЯХ.
 *
 * Неразобранная ручная сумма просто не входит в набор: пока оператор набирает
 * «1.», греть нечего, и отказывать всему набору из-за незакрытой точки нельзя.
 */
export function warmRequest(s: Settings): WarmRequest {
  const presets = normalizeSizes(s.sizes).sizes;

  const custom = parse(s.customSize);
  if (custom === null || custom <= 0) {
    return { sizes_usd: presets, presets_usd: presets };
  }
  // Потолок здесь MAX_WARM_SIZES, а не MAX_PRESETS: девять пресетов плюс ручная
  // сумма — законный набор, и резать его по девяти значило бы выбросить ровно ту
  // сумму, ради которой всё это и делается.
  const sizes = normalizeSizes([...presets, format(custom)], MAX_WARM_SIZES).sizes;
  return { sizes_usd: sizes, presets_usd: presets };
}

/**
 * Один и тот же ли это запрос.
 *
 * Нужен вызывающему, чтобы не переписывать ключ на каждое нажатие клавиши в поле
 * ввода. Сравнение по канонической форме, а не по ссылке: zustand отдаёт новый
 * объект настроек на любую правку, включая ту, которая набора не меняет вовсе
 * (выбор пресета, боевой режим).
 */
export function sameWarmRequest(a: WarmRequest, b: WarmRequest): boolean {
  return (
    a.sizes_usd.join(",") === b.sizes_usd.join(",") &&
    a.presets_usd.join(",") === b.presets_usd.join(",")
  );
}
